import React, { useEffect, useMemo } from 'react';

import * as yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import defaultClient from '@src/api/clients/default';
import { FormBase, Submit, TextField } from '@src/components/form';
import { useAuth } from '@src/context';
import { useMessage } from '@src/context/common';
import useForm from '@src/hooks/useForm';

const profileSchema = yup.object().shape({
  first_name: yup.string().max(150).label('First name'),
  last_name: yup.string().max(150).label('Last name'),
});

const emailSchema = yup.object().shape({
  email: yup.string().email('Enter a valid email').required('Email is required'),
});

const passwordSchema = yup.object().shape({
  old_password: yup.string().required('Current password is required'),
  new_password1: yup.string().min(8, 'Use at least 8 characters').required('New password is required'),
  new_password2: yup
    .string()
    .oneOf([yup.ref('new_password1'), null], 'Passwords must match')
    .required('Please confirm your new password'),
});

const resetSchema = yup.object().shape({
  email: yup.string().email('Enter a valid email').required('Email is required'),
});

export default function AccountPage() {
  const { state, authenticate } = useAuth();
  const message = useMessage();
  const user = state?.user || {};

  const profileForm = useForm({ schema: profileSchema, defaultValues: { first_name: '', last_name: '' } });
  const emailForm = useForm({ schema: emailSchema, defaultValues: { email: '' } });
  const passwordForm = useForm({ schema: passwordSchema, defaultValues: { old_password: '', new_password1: '', new_password2: '' } });
  const resetForm = useForm({ schema: resetSchema, defaultValues: { email: '' } });

  const userName = useMemo(() => {
    const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return full || 'Your account';
  }, [user.first_name, user.last_name]);

  useEffect(() => {
    profileForm.reset({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
    });
    emailForm.reset({ email: user.email || '' });
    resetForm.reset({ email: user.email || '' });
  }, [emailForm, profileForm, resetForm, user.email, user.first_name, user.last_name]);

  const refreshUser = (data) => {
    const current = state || {};
    authenticate({ ...current, user: data });
  };

  const handleProfileSubmit = ({ first_name, last_name }) =>
    defaultClient
      .request({
        url: 'accounts/auth/user/',
        method: 'PATCH',
        payload: { first_name, last_name },
      })
      .then((resp) => {
        const updatedUser = resp?.data || resp;
        refreshUser(updatedUser);
        message.success('Profile updated');
      })
      .catch(() => {
        message.error('Unable to update profile');
      });

  const handleEmailSubmit = ({ email }) =>
    defaultClient
      .request({
        url: 'accounts/auth/user/',
        method: 'PATCH',
        payload: { email },
      })
      .then((resp) => {
        const updatedUser = resp?.data || resp;
        refreshUser(updatedUser);
        message.success('Email updated');
      })
      .catch(() => {
        message.error('Unable to update email');
      });

  const handlePasswordSubmit = (payload) =>
    defaultClient
      .request({
        url: 'accounts/auth/password/change/',
        method: 'POST',
        payload,
      })
      .then(() => {
        message.success('Password updated');
        passwordForm.reset({ old_password: '', new_password1: '', new_password2: '' });
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail || 'Unable to change password';
        message.error(detail);
      });

  const handleResetRequest = ({ email }) =>
    defaultClient
      .request({
        auth: false,
        url: 'accounts/auth/password/reset/',
        method: 'POST',
        payload: { email },
      })
      .then(() => {
        message.success('Password reset link sent if the email exists');
      })
      .catch(() => {
        message.error('Unable to send reset link');
      });

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4">Account</Typography>
        <Typography color="text.secondary" variant="body2">
          Update your personal details and manage access.
        </Typography>
      </Box>

      <Card>
        <CardHeader title="Profile" subheader="Update your name" />
        <Divider />
        <CardContent>
          <FormBase onSubmit={profileForm.handleSubmit({ onSubmit: handleProfileSubmit })}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  control={profileForm.control}
                  fullWidth
                  label="First name"
                  name="first_name"
                  autoComplete="given-name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  control={profileForm.control}
                  fullWidth
                  label="Last name"
                  name="last_name"
                  autoComplete="family-name"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Submit variant="contained">Save profile</Submit>
            </Box>
          </FormBase>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Email" subheader="Change the email you use to sign in" />
        <Divider />
        <CardContent>
          <FormBase onSubmit={emailForm.handleSubmit({ onSubmit: handleEmailSubmit })}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField control={emailForm.control} fullWidth label="Email" name="email" autoComplete="email" />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Submit variant="contained">Update email</Submit>
            </Box>
          </FormBase>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Password" subheader="Set a new password for your account" />
        <Divider />
        <CardContent>
          <FormBase onSubmit={passwordForm.handleSubmit({ onSubmit: handlePasswordSubmit })}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  control={passwordForm.control}
                  fullWidth
                  label="Current password"
                  name="old_password"
                  type="password"
                  autoComplete="current-password"
                />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6}>
                <TextField
                  control={passwordForm.control}
                  fullWidth
                  label="New password"
                  name="new_password1"
                  type="password"
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  control={passwordForm.control}
                  fullWidth
                  label="Confirm new password"
                  name="new_password2"
                  type="password"
                  autoComplete="new-password"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Submit variant="contained">Update password</Submit>
            </Box>
          </FormBase>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Password reset link" subheader="Send yourself a reset link if you’re locked out" />
        <Divider />
        <CardContent>
          <FormBase onSubmit={resetForm.handleSubmit({ onSubmit: handleResetRequest })}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  control={resetForm.control}
                  fullWidth
                  label="Email"
                  name="email"
                  autoComplete="email"
                  helperText="We’ll send a reset link to this address."
                />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button type="submit" fullWidth variant="outlined">
                  Send reset link
                </Button>
              </Grid>
            </Grid>
          </FormBase>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Signed in as" />
        <Divider />
        <CardContent>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">{userName}</Typography>
            <Typography color="text.secondary" variant="body2">
              {user.email}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

AccountPage.displayName = 'AccountPage';
